import type { Problem } from "./problem-model";
import type { Definition } from "./types";

/** `step welcome: ...` (the first dot-separated part is the step; a rule's action is `step.action`). */
const STEP_LINE = /^step ([a-z0-9_.]+): (.*)$/s;
/** `paths.A.steps.0.buttons.1.goto: ...` or `paths.B: ...`. */
const PATH_LINE = /^(paths\.([A-Z]{1,2})(?:\.steps\.(\d+))?[a-z0-9_.]*)(?:: (.*))?$/s;

/** The path whose steps (or whose Apply rules actions) include the step id. */
function pathOfStep(definition: Definition, stepId: string): string | undefined {
  return Object.entries(definition.paths).find(([, path]) => path.steps.some((step) => step.id === stepId || (step.type === "apply_rules" && step.rules.some((rule) => rule.actions.some((action) => action.id === stepId)))))?.[0];
}

/** The step that holds the id: the step itself, or the Apply rules step an action sits in. */
function ownerOf(definition: Definition, id: string): string {
  for (const path of Object.values(definition.paths)) {
    for (const step of path.steps) {
      if (step.id === id || (step.type === "apply_rules" && step.rules.some((rule) => rule.actions.some((action) => action.id === id)))) return step.id;
    }
  }
  return id;
}

/** One line of a refused save as a problem placed on the step or path it names (settings, translations and other lines have no place). */
export function serverProblemOf(line: string, definition: Definition): Problem {
  const byStep = STEP_LINE.exec(line);
  if (byStep?.[1]) {
    const named = byStep[1].split(".")[0] ?? byStep[1];
    const stepId = ownerOf(definition, named);
    return { code: "server", where: `step ${named}`, path: pathOfStep(definition, stepId), stepId, params: { text: byStep[2] ?? line }, source: "server" };
  }
  const byPath = PATH_LINE.exec(line);
  if (byPath?.[2]) {
    const stepId = byPath[3] === undefined ? undefined : definition.paths[byPath[2]]?.steps[Number(byPath[3])]?.id;
    const where = byPath[1] ?? line;
    // A bare location (`paths.A.steps.0.send_message.text`) is how the server says a field is missing or not valid.
    if (byPath[4] === undefined && stepId !== undefined) return { code: "invalid_field", where, path: byPath[2], stepId, params: { field: where.split(".").at(-1) ?? where }, source: "server" };
    return { code: "server", where, path: byPath[2], stepId, params: { text: byPath[4] ?? line }, source: "server" };
  }
  return { code: "server", where: line.split(": ", 1)[0] ?? line, params: { text: line }, source: "server" };
}

/** The problems of a refused save, each on the step or path it names. */
export const serverProblemsOf = (lines: readonly string[], definition: Definition): Problem[] => lines.map((line) => serverProblemOf(line, definition));

/**
 * A problem the server reported belongs to the version that was refused: once the step (or path) it names has been
 * edited it may be gone, so it stops being shown. Editing back to what was refused brings it back.
 */
export function stillApplies(problem: Problem, refused: Definition, current: Definition): boolean {
  if (problem.stepId !== undefined && problem.path !== undefined) {
    const was = refused.paths[problem.path]?.steps.find((step) => step.id === problem.stepId);
    const now = current.paths[problem.path]?.steps.find((step) => step.id === problem.stepId);
    return was !== undefined && was === now;
  }
  if (problem.path !== undefined) return refused.paths[problem.path] !== undefined && refused.paths[problem.path] === current.paths[problem.path];
  return refused.settings === current.settings && refused.translations === current.translations;
}
